import {
  Dialog,
  DialogContent,
  Grid,
  Slide,
  CircularProgress,
} from "@mui/material";
import { useState, useEffect, useRef } from "react";
import { apiClient } from "api/apiClient";
import { COLORS, SIZES, WEIGHT } from "theme";
import Text from "ReusableComponents/Text";
import Button from "ReusableComponents/Button";
import Close from "assets/close.svg";
import CitiesJson from "assets/json/india-state-city.json";
import { useDispatch } from "react-redux";
import { closeEnquiry } from "store/actions/enquiryActions";

const Enquire = ({ open, small = false }) => {
  const dispatch = useDispatch();
  const [stateEnquire, setStateEnquire] = useState({
    name: "",
    phone: "",
    state: "",
    city: "",
    productType: "",
    utm_medium: "",
    utm_source: "",
    utm_campaign: "",
    gclid: "",
  });
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState({}); // per-field errors
  const [isClosing, setIsClosing] = useState(false);

  const handleClose = () => {
    dispatch(closeEnquiry());
  };

  // Animated close helper
  const handleAnimatedClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      handleClose();
    }, 400);
  };

  // fetch products once
  useEffect(() => {
    fetch("https://cms.eulermotors.com/api/products?populate=*")
      .then((r) => r.json())
      .then((js) => setOptions(js.data))
      .catch(console.error);
  }, []);

  // grab UTM params once
  useEffect(() => {
    const utm_medium = sessionStorage.getItem("utm_medium");
    const utm_source = sessionStorage.getItem("utm_source");
    const utm_campaign = sessionStorage.getItem("utm_campaign");
    const gclid = sessionStorage.getItem("gclid");
    if (utm_medium || utm_source || utm_campaign || gclid) {
      setStateEnquire((prev) => ({
        ...prev,
        utm_medium,
        utm_source,
        utm_campaign,
        gclid,
      }));
    }
  }, []);

  const validateName = (n) => /^[A-Za-z ]+$/.test(n);
  const validatePhone = (p) => /^\d{10}$/.test(p);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setStateEnquire((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "state" ? { city: "" } : {}),
    }));
    if (errors[name]) {
      setErrors((prev) => {
        const { [name]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleSubmit = () => {
    const { name, phone, state, city, productType } = stateEnquire;
    const newErr = {};

    if (!name) newErr.name = "Name is required.";
    else if (!validateName(name))
      newErr.name = "Only letters and spaces allowed.";

    if (!phone) newErr.phone = "Mobile is required.";
    else if (!validatePhone(phone)) newErr.phone = "Must be a 10-digit number.";

    if (!state) newErr.state = "State is required.";
    if (!city) newErr.city = "City is required.";
    if (!productType) newErr.productType = "Please select a product.";

    if (Object.keys(newErr).length) {
      setErrors(newErr);
      return;
    }

    setErrors({});
    setLoading(true);
    apiClient
      .sendEnquireFormData({
        name,
        phone_number: phone,
        state,
        city,
        product: productType,
        description: "Enquire-Form",
        utm_medium: stateEnquire.utm_medium,
        utm_source: stateEnquire.utm_source,
        utm_campaign: stateEnquire.utm_campaign,
        gclid: stateEnquire.gclid,
      })
      .finally(() => {
        setLoading(false);
        setSuccess(true);
        // show thank-you, then animate close
        setTimeout(() => handleAnimatedClose(), 1500);
      });
  };

  const statesList = Object.keys(CitiesJson).sort();
  const citiesList = stateEnquire.state
    ? CitiesJson[stateEnquire.state].sort()
    : [];

  // compact size tokens
  const contentPadding = small ? 1 : 2;
  const gridSpacing = small ? 1 : 2;
  const inputPadding = small ? "4px 8px" : "8px 16px";
  const labelFont = small ? SIZES.twelve : SIZES.sixteen;
  const inputFont = small ? SIZES.twelve : SIZES.sixteen;
  const formHeadingFont = small ? SIZES.twenty : SIZES.twentyFour;

  return (
    <Dialog
      open={open && !isClosing}
      onClose={handleAnimatedClose}
      fullWidth
      maxWidth="sm"
      TransitionComponent={Slide}
      TransitionProps={{ direction: "up", timeout: 400 }}
      PaperProps={{
        sx: {
          borderRadius: "10px",
          background: "#F4F4F4",
          boxShadow: "0px 6px 36px #0000001A",
          m: small ? 1 : 2,
        },
      }}
    >
      <DialogContent sx={{ p: contentPadding, position: "relative" }}>
        <img
          src={Close}
          alt="close"
          onClick={handleAnimatedClose}
          style={{
            position: "absolute",
            top: 8,
            right: 8,
            width: 24,
            cursor: "pointer",
          }}
        />

        <Grid container spacing={gridSpacing}>
          {/* Title */}
          <Grid item xs={12}>
            <Text fontSize={formHeadingFont} fontWeight="bold" margin={0}>
              Enquire Now
            </Text>
          </Grid>

          {/* Name */}
          <Grid item xs={12} sm={6}>
            <Text fontSize={labelFont} fontWeight="bold">
              Name *
            </Text>
            <input
              name="name"
              value={stateEnquire.name}
              onChange={handleChange}
              placeholder="Enter Name"
              style={{
                width: "100%",
                fontSize: inputFont,
                padding: inputPadding,
                borderRadius: 4,
                border: `1px solid ${
                  errors.name ? COLORS.red : COLORS.lightBlack
                }`,
              }}
            />
            {errors.name && (
              <Text
                textColor={COLORS.red}
                fontWeight={WEIGHT.bold}
                fontSize="12px"
                margin="4px 0 0"
              >
                {errors.name}
              </Text>
            )}
          </Grid>

          {/* Mobile */}
          <Grid item xs={12} sm={6}>
            <Text fontSize={labelFont} fontWeight="bold">
              Mobile *
            </Text>
            <input
              name="phone"
              value={stateEnquire.phone}
              onChange={handleChange}
              placeholder="Enter Number"
              style={{
                width: "100%",
                fontSize: inputFont,
                padding: inputPadding,
                borderRadius: 4,
                border: `1px solid ${
                  errors.phone ? COLORS.red : COLORS.lightBlack
                }`,
              }}
            />
            {errors.phone && (
              <Text
                textColor={COLORS.red}
                fontSize="12px"
                fontWeight={WEIGHT.bold}
                margin="4px 0 0"
              >
                {errors.phone}
              </Text>
            )}
          </Grid>

          {/* State */}
          <Grid item xs={12} sm={6}>
            <Text fontSize={labelFont} fontWeight="bold">
              State *
            </Text>
            <select
              name="state"
              value={stateEnquire.state}
              onChange={handleChange}
              style={{
                width: "100%",
                fontSize: inputFont,
                padding: inputPadding,
                borderRadius: 4,
                border: `1px solid ${
                  errors.state ? COLORS.red : COLORS.lightBlack
                }`,
                background: COLORS.white,
              }}
            >
              <option value="">Select State</option>
              {statesList.map((s) => (
                <option key={s}>{s}</option>
              ))}
            </select>
            {errors.state && (
              <Text
                textColor={COLORS.red}
                fontWeight={WEIGHT.bold}
                fontSize="12px"
                margin="4px 0 0"
              >
                {errors.state}
              </Text>
            )}
          </Grid>

          {/* City */}
          <Grid item xs={12} sm={6}>
            <Text fontSize={labelFont} fontWeight="bold">
              City *
            </Text>
            <select
              name="city"
              value={stateEnquire.city}
              onChange={handleChange}
              style={{
                width: "100%",
                fontSize: inputFont,
                padding: inputPadding,
                borderRadius: 4,
                border: `1px solid ${
                  errors.city ? COLORS.red : COLORS.lightBlack
                }`,
                background: COLORS.white,
              }}
            >
              <option value="">Select City</option>
              {citiesList.map((c) => (
                <option key={c}>{c}</option>
              ))}
            </select>
            {errors.city && (
              <Text
                textColor={COLORS.red}
                fontWeight={WEIGHT.bold}
                fontSize="12px"
                margin="4px 0 0"
              >
                {errors.city}
              </Text>
            )}
          </Grid>

          {/* Product */}
          <Grid item xs={12}>
            <Text fontSize={labelFont} fontWeight="bold">
              Product *
            </Text>
            <select
              name="productType"
              value={stateEnquire.productType}
              onChange={handleChange}
              style={{
                width: "100%",
                fontSize: inputFont,
                padding: inputPadding,
                borderRadius: 4,
                border: `1px solid ${
                  errors.productType ? COLORS.red : COLORS.lightBlack
                }`,
                background: COLORS.white,
              }}
            >
              <option value="">Select Product</option>
              {options.map((o) => (
                <option key={o.id}>{o.attributes.title}</option>
              ))}
            </select>
            {errors.productType && (
              <Text
                textColor={COLORS.red}
                fontWeight={WEIGHT.bold}
                fontSize="12px"
                margin="4px 0 0"
              >
                {errors.productType}
              </Text>
            )}
          </Grid>

          {/* Submit / Success */}
          <Grid item xs={12} textAlign="center">
            {loading ? (
              <CircularProgress size={24} />
            ) : success ? (
              <Text
                color={COLORS.green}
                fontSize={inputFont}
                fontWeight={WEIGHT.bold}
              >
                Thank you for contacting us!
              </Text>
            ) : (
              <Button
                background="#9CCDFB"
                hoverBackground
                cursor="pointer"
                margin="16px auto"
                padding={small ? "8px 16px" : "12px 24px"}
                fontSize={inputFont}
                onClick={handleSubmit}
              >
                Submit
              </Button>
            )}
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default Enquire;
